import React, { useState } from 'react';
import { useAgentChat } from '../hooks/useAgentChat';
import MessageList from '../components/chat/MessageList';
import ChatInput from '../components/chat/ChatInput';
import LearningStatsPanel from '../components/agent/LearningStatsPanel';
import ActiveWordsPanel from '../components/agent/ActiveWordsPanel';
import { Modal } from '../components/common/Modal';
import { Button } from '../components/common/Button';
import { LoadingSpinner } from '../components/common/LoadingSpinner';

/**
 * LearnPage - Practice mode interface
 *
 * Locked to practice mode: AI picks a word from the user's vocabulary list,
 * asks them to write a sentence, then checks usage and gives feedback.
 */
const LearnPage = () => {
  const {
    messages,
    settings,
    learningStats,
    activeWords,
    isLoading,
    isSending,
    error,
    hasMore,
    sendMessage,
    clearConversation,
    loadMore,
    clearError
  } = useAgentChat();

  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [showStats, setShowStats] = useState(false);

  /**
   * Handle sending a message — always in practice mode
   */
  const handleSendMessage = async (message) => {
    try {
      await sendMessage(message, 'practice');
    } catch (err) {
      console.error('Error sending message:', err);
    }
  };

  /**
   * Handle clearing conversation
   */
  const handleClearConversation = async () => {
    setIsClearing(true);

    try {
      await clearConversation();
      setShowClearConfirm(false);
    } catch (err) {
      console.error('Error clearing conversation:', err);
    } finally {
      setIsClearing(false);
    }
  };

  /**
   * Show initial loading state
   */
  if (isLoading && !settings) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-white rounded-lg shadow-sm h-full">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-gray-200">
        <div className="px-4 py-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-700">Practice</h2>
          <div className="flex items-center gap-2">
            {/* Stats toggle */}
            <button
              onClick={() => setShowStats(!showStats)}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              title="Learning Statistics"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </button>
          </div>
        </div>

        {/* Stats panel (collapsible) */}
        {showStats && learningStats && (
          <div className="px-4 pb-3">
            <LearningStatsPanel stats={learningStats} />
          </div>
        )}

        {/* Active words panel */}
        {activeWords && activeWords.length > 0 && (
          <div className="px-4 pb-3 border-t border-gray-100 pt-2">
            <ActiveWordsPanel words={activeWords} />
          </div>
        )}
      </div>

      {/* Error banner */}
      {error && (
        <div className="flex-shrink-0 bg-red-50 border-b border-red-200 px-6 py-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 flex-1">
              <svg
                className="w-5 h-5 text-red-600 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span className="text-sm text-red-700">{error}</span>
            </div>
            <button
              onClick={clearError}
              className="p-1 text-red-500 hover:text-red-700 hover:bg-red-100 rounded"
              title="Dismiss"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Messages area */}
      <div className="flex-1 overflow-hidden">
        {messages.length === 0 && !isLoading ? (
          <div className="flex-1 overflow-y-auto px-4 py-6 h-full">
            <div className="flex gap-3 max-w-2xl">
              {/* Bot avatar */}
              <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-primary-600 text-sm">✦</span>
              </div>
              {/* Bubble */}
              <div className="bg-gray-100 rounded-2xl rounded-tl-sm px-4 py-3 max-w-lg">
                <p className="text-gray-800 font-medium mb-1">Ready to practice? 👋</p>
                <p className="text-gray-600 text-sm">
                  I'll pick words from your vocabulary list and ask you to write a sentence
                  using each one. After you write it, I'll check your usage and give you feedback.
                </p>
                <p className="text-gray-600 text-sm mt-2">
                  Type <strong>Start</strong> whenever you're ready!
                </p>
              </div>
            </div>
          </div>
        ) : (
          <MessageList
            messages={messages}
            isLoading={isLoading}
            hasMore={hasMore}
            onLoadMore={loadMore}
          />
        )}
      </div>

      {/* Input area */}
      <div className="flex-shrink-0 border-t border-gray-200">
        <ChatInput
          onSend={handleSendMessage}
          isSending={isSending}
          disabled={false}
          onClear={() => setShowClearConfirm(true)}
          placeholder="Write your sentence here..."
        />
      </div>

      {/* Clear confirmation modal */}
      <Modal
        isOpen={showClearConfirm}
        onClose={() => setShowClearConfirm(false)}
        title="Clear Conversation"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Are you sure you want to clear all messages? This action cannot be undone.
          </p>
          <div className="flex gap-3 justify-end">
            <Button
              variant="outline"
              onClick={() => setShowClearConfirm(false)}
              disabled={isClearing}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleClearConversation}
              disabled={isClearing}
            >
              {isClearing ? 'Clearing...' : 'Clear All'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default LearnPage;
